import PropTypes from 'prop-types'
import styled from 'styled-components'
import { forwardRef } from 'react'

import { base, vagRundFont } from '../../mixins'
import { BLACK } from '../../colors'

const weights = {
  normal: 400,
  bold: 700,
  light: 300
}
const level5Weights = {
  normal: 700,
  bold: 900,
  light: 400
}
const levels = {
  1: {
    fontSize: '36px',
    lineHeight: '40px',
    marginBottom: '20px'
  },
  2: {
    fontSize: '30px',
    lineHeight: '34px',
    marginBottom: '20px'
  },
  3: {
    fontSize: '24px',
    lineHeight: '28px',
    marginBottom: '20px'
  },
  4: {
    fontSize: '20px',
    lineHeight: '24px',
    marginBottom: '20px'
  },
  5: {
    fontSize: '16px',
    lineHeight: '18px',
    marginBottom: '20px'
  }
}
const getMargin = ({ removeMargins, level, mt, mr, mb, ml }) => {
  if (removeMargins) {
    return 'margin: 0'
  }

  return `
    margin-top: ${mt || 0};
    margin-right: ${mr || 0};
    margin-bottom: ${mb || levels[level].marginBottom};
    margin-left: ${ml || 0};
  `
}
/**
 * Calculates letter-spacing for Vag Rund font based on props
 * and our preferred style for Vag Rund.
 *
 * If we wanted to finesse the spacing based on browser, we
 * could do that here as well.
 */
const getLetterSpacing = ({ weight, level, variant }) => {
  if (!variant && (/bold/.test(weight) || parseInt(level) < 4)) {
    return '0.8px'
  }

  return 'normal'
}
const getMarking = ({ mark, markColor }) => {
  if (!mark) {
    return null
  }

  return `
    ${mark} {
      color: ${markColor};
    }
  `
}
const getWeight = ({ level, weight }) => {
  if (level === '5') {
    return level5Weights[weight]
  }

  return weights[weight]
}
/**
 * Overrides the default `weight` applied to the heading by
 * setting the weight of marked elements to the base weight
 * for Vag of 400. The default marked element is <span> if
 * not specified with the `mark` prop.
 */
const getMixedWeightVariant = ({ variant, mark }) => {
  if (variant !== 'mixed-weight') {
    return null
  }

  return `
    ${mark ?? 'span'} {
      font-weight: 400;
    }
  `
}
const StyledHeading = styled.h1`
  ${base};
  ${({ variant, level }) => (variant === 'font' || level === '5' ? null : vagRundFont)};
  font-size: ${({ level, fontSize }) => fontSize || levels[level].fontSize};
  font-weight: ${getWeight};
  font-style: ${({ isItalic }) => (isItalic ? 'italic' : 'normal')};
  line-height: ${({ level }) => levels[level].lineHeight};
  text-align: ${({ align }) => align};
  color: ${({ color }) => color};
  text-transform: ${({ transform }) => transform};
  letter-spacing: ${getLetterSpacing};
  ${getMargin};
  ${getMarking};
  ${getMixedWeightVariant};
`
/**
 * The Heading component can be used to create different level headings.
 */
const Heading = forwardRef(function Heading(
  {
    children,
    fontSize,
    mark,
    markColor,
    variant,
    level = '1',
    isItalic = false,
    color = BLACK,
    weight = 'normal',
    align = 'left',
    transform = 'none',
    removeMargins = false,
    ...rest
  },
  ref
) {
  return (
    <StyledHeading
      ref={ref}
      as={`h${level}`}
      level={level}
      color={color}
      variant={variant}
      mark={mark}
      markColor={markColor}
      fontSize={fontSize}
      transform={transform}
      isItalic={isItalic}
      weight={weight}
      align={align}
      removeMargins={removeMargins}
      {...rest}>
      {children}
    </StyledHeading>
  )
})

Heading.propTypes = {
  /** The Heading level */
  level: PropTypes.oneOf(['1', '2', '3', '4', '5']),
  /** The Heading color */
  color: PropTypes.string,
  /** Set the font size overriding the size for the `level` */
  fontSize: PropTypes.string,
  /** Sets the text-transform */
  transform: PropTypes.oneOf(['uppercase', 'lowercase', 'capitalize', 'none']),
  /** What font weight to apply to the Heading */
  weight: PropTypes.oneOf(['normal', 'bold', 'light']),
  /** How text should be aligned within the Heading */
  align: PropTypes.oneOf(['left', 'center', 'right']),
  /** Whether the font style is italic or normal */
  isItalic: PropTypes.bool,
  /** Applies a variant to the general styling */
  variant: PropTypes.oneOf(['font', 'mixed-weight']),
  /** Sets the top margin */
  mt: PropTypes.string,
  /** Sets the bottom margin */
  mb: PropTypes.string,
  /** Sets the right margin */
  mr: PropTypes.string,
  /** Sets the left margin */
  ml: PropTypes.string,
  /** Whether the margin has been removed */
  removeMargins: PropTypes.bool,
  /** What element, if any, inside the heading should be highlighted */
  mark: PropTypes.oneOf(['span', 'strong', 'em']),
  /** What color to use to highlight the marked element */
  markColor: PropTypes.string
}

export { Heading }
